import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, MessageSquareText, FileText, Lock, Sparkles, Check } from "lucide-react";
import ArticlePreview from "@/components/content/ArticlePreview";

export const useContentDefaults = (wsId) => {
  const [d, setD] = useState(null);
  useEffect(() => { if (wsId) api.get(`/content/defaults?workspace_id=${wsId}`).then((r) => setD(r.data)).catch(() => {}); }, [wsId]);
  return d;
};

const FormatOption = ({ active, locked, icon: Icon, title, sub, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex-1 text-left rounded-lg border p-3 transition-colors ${active ? "border-primary bg-primary/10" : "border-border hover:border-primary/40 bg-secondary/20"}`}>
    <div className="flex items-center gap-2 text-sm font-semibold"><Icon className="h-4 w-4 text-primary" /> {title}
      {locked && <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-amber-300 border border-amber-500/40 rounded px-1.5 py-0.5"><Lock className="h-3 w-3" /> E-Com+</span>}
    </div>
    <p className="text-xs text-muted-foreground mt-1">{sub}</p>
  </button>
);

export default function DraftBuilderModal({ open, onOpenChange, row, workspace, onUpgrade, onDone }) {
  const navigate = useNavigate();
  const defaults = useContentDefaults(open ? workspace?.id : null);
  const [format, setFormat] = useState("social");
  const [target, setTarget] = useState("");
  const [cta, setCta] = useState("");
  const [busy, setBusy] = useState(false);
  const [social, setSocial] = useState(null);
  const [article, setArticle] = useState(null);

  useEffect(() => { if (open) { setFormat("social"); setSocial(null); setArticle(null); } }, [open, row]);
  useEffect(() => { if (defaults && !target) { setTarget(defaults.target_url || ""); setCta(defaults.target_url || ""); } }, [defaults]); // eslint-disable-line

  const pickBlog = () => {
    if (defaults && !defaults.blog_allowed) { onUpgrade?.({ kind: "blog", message: "Long-form blog articles are on E-Commerce ($79/mo) and Agency ($199/mo). Creator includes social feed posts." }); return; }
    setFormat("blog");
  };

  const generate = async () => {
    if (format === "blog" && !target.trim()) { toast.error("Add your website URL so the article uses real facts"); return; }
    setBusy(true);
    try {
      if (format === "blog") {
        const r = await api.post("/keywords/blog-draft", { workspace_id: workspace.id, keyword: row.keyword, angle: row.angle, target_url: target, cta_url: cta || target, volume: row.volume, cpc: row.cpc, competition: row.competition });
        setArticle(r.data.item);
      } else {
        const r = await api.post("/keywords/draft", { workspace_id: workspace.id, keyword: row.keyword, angle: row.angle, platform: row.platform === "Facebook" ? "Facebook" : "LinkedIn", target_url: target || undefined, cta_url: cta || undefined });
        setSocial(r.data);
      }
      onDone?.(row);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 403 && d && typeof d === "object") onUpgrade?.({ kind: d.code === "blog_tier" ? "blog" : d.code === "keyword_draft_tier" ? "draft" : "credits", message: d.message });
      else toast.error(typeof d === "string" ? d : (d?.message || "Generation failed"));
    } finally { setBusy(false); }
  };

  const done = social || article;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-card border-border max-h-[92vh] overflow-y-auto" data-testid="draft-builder-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Draft from “{row?.keyword}”</DialogTitle>
          <DialogDescription>{row?.angle}</DialogDescription>
        </DialogHeader>
        {!done && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <FormatOption testid="format-social" active={format === "social"} icon={MessageSquareText} title="Social Media Feed Post" sub="Facebook / LinkedIn · hook, spec, one CTA" onClick={() => setFormat("social")} />
              <FormatOption testid="format-blog" active={format === "blog"} locked={defaults && !defaults.blog_allowed} icon={FileText} title="Long-Form Blog Article" sub="SEO & LinkedIn Article · H1, 3-4 sections, images, CTA" onClick={pickBlog} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Website to read facts & images from</Label>
                <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="https://yourstore.com" className="mt-1 bg-background" data-testid="draft-target-url" /></div>
              <div><Label className="text-xs">CTA link (clickable button)</Label>
                <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="https://yourstore.com/products/x" className="mt-1 bg-background" data-testid="draft-cta-url" /></div>
            </div>
            {defaults?.scrape && <p className="text-[11px] text-muted-foreground" data-testid="draft-scrape-quota">Website scans this month: {defaults.scrape.used}/{defaults.scrape.limit >= 1e6 ? "∞" : defaults.scrape.limit} · cached sites don't use a scan</p>}
            <Button onClick={generate} disabled={busy} className="w-full bg-primary hover:bg-primary/90 text-white h-11" data-testid="draft-generate-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {format === "blog" ? "Reading site and writing article…" : "Writing post…"}</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate {format === "blog" ? "article" : "post"}</>}
            </Button>
          </div>
        )}
        {social && (
          <div className="space-y-3" data-testid="draft-social-result">
            <p className="text-xs text-emerald-400 flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Saved as a draft on your calendar</p>
            {social.image_url && <img src={social.image_url} alt="" className="w-48 rounded-md border border-border" />}
            <p className="whitespace-pre-line text-sm rounded-lg border border-border bg-background p-4" data-testid="draft-social-text">{social.post_text}</p>
            {social.link_url && <a href={social.link_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline" data-testid="draft-social-link">{social.link_url}</a>}
            <Button size="sm" variant="outline" className="border-border" onClick={() => navigate("/calendar")} data-testid="draft-open-calendar">Open calendar</Button>
          </div>
        )}
        {article && <ArticlePreview item={article} shopifyConnected={defaults?.shopify_connected} onChange={setArticle} />}
      </DialogContent>
    </Dialog>
  );
}
